import { ICoreContext } from '@msdyn365-commerce/core';
import { getAttributeStringOrDefault, getAttributeText, getAttributesLocale } from './attributes';
import { getMostUsedAttributeNameIndex, sortAttributes, sortAttributesByGroupOrder } from '.';
import { AttributeDataType, AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { decodeBase64String, isBase64EncodePrefixed } from '../base64';
import { AttributeKey } from '../types';

export interface IAttributeGroupMapAttribute {
    name: AttributeKey | AttributeKey[];
    label?: string;
    hideIfEmpty?: boolean;
    transform?: (value: string | undefined, attributes: AttributeValue[] | undefined) => string | undefined;
    transformName?: (name: string, attributes: AttributeValue[] | undefined) => string;
}

export interface IAttributeGroupMap {
    type: AttributeGroupType;
    group: string;
    attributes?: (AttributeKey | IAttributeGroupMapAttribute)[];
    transform?: (value: string | undefined, attributes: AttributeValue[] | undefined) => string | undefined;
    defaultValue?: string;
    nullValues?: string[];
    /** The attribute to use as source. If set this will look for the attributes inside this attribute. */
    attributeSource?: string;
    attributeSourceSet?: { source: string, value: string }[];
    exclude?: string[];
}

export type AttributeGroupType = 'default' | 'allergens';
export type ModelVersion = '1.0' | '2.0' | '3.0';

export interface IParsedAttributeMap {
    type: AttributeGroupType;
    name: string;
    attributes: IParsedAttribute[];
    modelVersion?: ModelVersion;
}

export interface IParsedAttribute {
    name: string;
    value: string;
}

export const replaceCommaWithDot = (value: string | undefined): string | undefined => {
    if (!value) {
        return value;
    }

    // Shouldn't need to replace anything if both are present
    if (value.includes(',') && value.includes('.')) {
        return value.replace(' ', '');
    }

    // Only replace the last occurance of a comma with a dot
    const lastCommaIndex = value.lastIndexOf(',');
    if (lastCommaIndex !== -1) {
        return `${value.substring(0, lastCommaIndex)}.${value.substring(lastCommaIndex + 1)}`.replace(' ', '');
    }

    // If no comma is present, return the value as is
    return value.replace(' ', '');

    // return value.replace(',', '.').replace(' ', '');
};
export const padValue = (value: string | undefined): string | undefined => {
    if (!value) {
        return value;
    }
    return Number(value.replace(',', '')).toFixed(2).toString();
};
export const addCelsiusSign = (value: string | undefined): string | undefined => {
    return value ? `${value}°` : value;
};
export const addDashPrefix = (key: string): string => {
    return ` - ${key}`;
};
export const addLeftPaddingHTML = (value: string): string => {
    return `\t${value}`;
};

function findAttribute(attributeName: string): (attribute: string | IAttributeGroupMapAttribute) => boolean {
    return (attribute: string | IAttributeGroupMapAttribute) => {
        if (typeof attribute === 'string') {
            return attribute === attributeName;
        } else {
            return attribute.name === attributeName;
        }
    };
}

export function parseAttributesMap(context: ICoreContext, attributes: AttributeValue[] | undefined, attributesMap?: IAttributeGroupMap[]): IParsedAttributeMap[] {
    if (!attributesMap) {
        attributesMap = getAttributesMap(context);
    }

    const attributesLocale = getAttributesLocale(context.request.locale);
    const groups: {
        [key: string]: AttributeValue[];
    } = {};
    attributes = JSON.parse(JSON.stringify(attributes));

    if (attributes) {
        for (let i = 0; i < attributes.length; i++) {
            const attribute = attributes[i];
            if (isBase64EncodePrefixed(attribute)) {
                attribute.Name = attribute.Name?.replace('{base64}:', '');
                attribute.KeyName = attribute.KeyName?.replace('{base64}:', '');

                if (attribute.DataTypeValue === AttributeDataType.Text && attribute.TextValue) {
                    attribute.TextValue = attribute.TextValue.split('|').map(decodeBase64String).join('|');
                }
            }
        }
    }

    attributes!.sort(sortAttributesByGroupOrder).map((product: AttributeValue, index) => {
        const attrFind = product.ExtensionProperties?.find((e: any) => {
            return e.Key === 'ATTRGROUPNAME';
        });
        if(attrFind && attrFind.Value && attrFind.Value.StringValue){
            const key = attrFind.Value.StringValue.trim();
            if (groups[key]) {
                groups[key].push(product);
            } else {
                groups[key] = [];
                groups[key].push(product);
            }
        }
    });

    const parsed: IParsedAttributeMap[] = attributesMap.map((attributeGroup) => {
        const mostCommonNameIndex = getMostUsedAttributeNameIndex(context.request.locale, attributes, attributeGroup, attributesLocale.notFound);

        if (!attributeGroup.attributes && !groups[attributeGroup.group]) {
            return {
                type: attributeGroup.type,
                name: attributeGroup.group,
                attributes: []
            };
        }

        let modelVersion: ModelVersion = '1.0';
        let attributesToUse: AttributeValue[] | undefined;
        const attributeSource = attributeGroup.attributeSource
            ? attributes!.find((attribute: AttributeValue) => attribute.Name === attributeGroup.attributeSource)
            : undefined;
        const shouldUseAttributeSet = attributeGroup.attributeSourceSet?.some((source) => {
            return attributes?.find((attribute: AttributeValue) => attribute.Name === source.source)?.TextValue;
        });

        if (shouldUseAttributeSet) {
            modelVersion = '3.0';
            attributesToUse = [];

            attributeGroup.attributeSourceSet!.forEach((source) => {
                const attribute = attributes?.find((attribute: AttributeValue) => attribute.Name === source.source);

                if (!attribute || !attribute.TextValue) {
                    return;
                }

                attributesToUse!.push(...attribute.TextValue.split('|').map((attribute) => {
                    return {
                        Name: attribute,
                        DataTypeValue: 5,
                        TextValue: source.value,
                        ExtensionProperties: [
                            {
                                Key: 'ATTRORDER',
                                Value: {
                                    DecimalValue: (attributeGroup.attributes?.findIndex((attr) => {
                                        if (typeof attr === 'string') {
                                            return attr === attribute;
                                        } else {
                                            return attr.name === attribute;
                                        }
                                    }) ?? -1) + 1
                                }
                            }
                        ]
                    } as AttributeValue;
                }));
            });
        } else if (attributeGroup.attributeSource && attributeSource?.TextValue) {
            modelVersion = '2.0';
            attributesToUse = attributeSource?.TextValue?.split('|').map((attribute) => {
                return {
                    Name: attribute.split(':')[0],
                    DataTypeValue: 5,
                    TextValue: attribute.split(':')[1]
                } as AttributeValue;
            });
        } else {
            attributesToUse = attributes;
        }

        const attrs = !shouldUseAttributeSet && attributeGroup.attributes
            ? attributeGroup.attributes.map((attribute: IAttributeGroupMapAttribute | AttributeKey) => {
                if (typeof attribute === 'string') {
                    let value = getAttributeStringOrDefault(context.request.locale, attributesToUse, attribute , attributesLocale.notFound);

                    if (attributeGroup.transform) {
                        value = attributeGroup.transform(value, attributes) || '';
                    }

                    return {
                        name: getAttributeText(context.request.locale, attribute),
                        value
                    };
                } else {
                    let name: string;
                    let value: string | undefined;

                    if (typeof attribute.name === 'string') {
                        name = attribute.label || getAttributeText(context.request.locale, attribute.name) || attribute.name;
                        value = getAttributeStringOrDefault(context.request.locale, attributesToUse, attribute.name , attributeGroup.defaultValue);
                    } else {
                        const values: {
                            [key: string]: string | undefined
                        } = {};

                        attribute.name.forEach((name: string) => {
                            values[name] = getAttributeStringOrDefault(context.request.locale, attributesToUse, name as AttributeKey, attributeGroup.defaultValue);
                        });

                        if (Object.values(values).every((v) => !v || attributeGroup.nullValues?.includes(v))) {
                            name = getAttributeText(context.request.locale, attribute.name[mostCommonNameIndex]) || attribute.name[mostCommonNameIndex];
                            value = attributeGroup.defaultValue;
                        } else {
                            const found = Object.entries(values).find(([key, val]) => val && !attributeGroup.nullValues?.includes(val));

                            if (!found) {
                                name = attribute.label || getAttributeText(context.request.locale, attribute.name[mostCommonNameIndex]) || attribute.name[mostCommonNameIndex];
                                value = attributeGroup.defaultValue;
                            } else {
                                name = attribute.label || getAttributeText(context.request.locale, found[0] as AttributeKey) || found[0];
                                value = found[1];
                            }
                        }
                    }

                    if (attribute.transformName) {
                        name = attribute.transformName(name, attributes);
                    }

                    if (attribute.transform) {
                        value = attribute.transform(value, attributes);
                    } else if (attributeGroup.transform) {
                        value = attributeGroup.transform(value, attributes);
                    }

                    if (attribute.hideIfEmpty === true && value === undefined) {
                        return undefined;
                    }

                    return {
                        name,
                        value: value || attributesLocale.notFound
                    };
                }
            })
            : ((shouldUseAttributeSet || attributeGroup.attributeSource && attributeSource?.TextValue) ? attributesToUse! : groups[attributeGroup.group]).sort(sortAttributes).map((attribute: AttributeValue) => {
                if (!attribute.Name) {
                    throw new Error(`Attribute name is missing.`);
                }

                if (attributeGroup.exclude && attributeGroup.exclude.includes(attribute.Name)) {
                    return undefined;
                }

                let name = attribute.Name;
                /* let value = getAttributeStringOrDefault(context.request.locale,
                    attributesToUse,
                    attribute.Name as AttributeKey,
                    attributeGroup.defaultValue || attributesLocale.notFound); */
                let value = attribute.TextValue || attributeGroup.defaultValue || attributesLocale.notFound;

                if (attributeGroup.transform) {
                    value = attributeGroup.transform(value, attributes) || '';
                }

                const findAttributeFromAttributes = attributeGroup.attributes?.find(findAttribute(attribute.Name));

                if (typeof findAttributeFromAttributes === 'object') {
                    if (findAttributeFromAttributes.transformName) {
                        name = findAttributeFromAttributes.transformName(attribute.Name, attributes);
                    }
                }

                return {
                    name,
                    value
                };
            });

        return {
            type: attributeGroup.type,
            name: attributeGroup.group,
            attributes: attrs.filter(x => x).map((attribute) => {
                const { name, value } = attribute || {};

                return {
                    name: name as string,
                    value: value as string
                };
            }),
            modelVersion
        };
    });

    return parsed;
}

export function getAttributesMap(context: ICoreContext): IAttributeGroupMap[] {
    const attributesLocale = getAttributesLocale(context.request.locale);

    return [
        {
            type: 'default',
            group: attributesLocale.groups.productInformation,
            attributes: [
                {
                    name: 'importerGrouping',
                    transform: (value, attributes) => {
                        const alcoholProduct = parseInt(getAttributeStringOrDefault(context.request.locale, attributes, 'alcoholProduct', '0'), 10) || 0;

                        // Hide if not an alcohol product
                        return alcoholProduct === 0 ? undefined : value;
                    },
                    hideIfEmpty: true
                },
                {
                    name: 'agentImporter',
                    transformName(name, attributes) {
                        const alcoholProduct = parseInt(getAttributeStringOrDefault(context.request.locale, attributes, 'alcoholProduct', '0'), 10) || 0;

                        return alcoholProduct > 0 ? attributesLocale.labels.importer : name;
                    },
                    hideIfEmpty: true
                },
                {
                    name: 'producer',
                    transformName: (name, attributes) => {
                        const alcoholProduct = parseInt(getAttributeStringOrDefault(context.request.locale, attributes, 'alcoholProduct', '0'), 10) || 0;

                        return alcoholProduct > 0 ? attributesLocale.labels.manufacturer : name;
                    },
                    hideIfEmpty: true
                },
                {
                    name: 'brand',
                    hideIfEmpty: true
                },
                {
                    name: 'manufacturer',
                    label: attributesLocale.labels.manufacturer,
                    transformName: (name, attributes) => {
                        const alcoholProduct = parseInt(getAttributeStringOrDefault(context.request.locale, attributes, 'alcoholProduct', '0'), 10) || 0;

                        return alcoholProduct > 0 ? attributesLocale.labels.distributor : name;
                    },
                    hideIfEmpty: true
                },
                {
                    name: 'temperatureMin',
                    transform: addCelsiusSign
                },
                {
                    name: 'temperatureMax',
                    transform: addCelsiusSign
                },
                'gtin',
                'countryOfOrigin'
            ]
        },
        {
            type: 'default',
            group: attributesLocale.groups.nutrients,
            transform: (value) => {
                value = replaceCommaWithDot(value);
                value = padValue(value);
                return value;
            },
            defaultValue: '0.00',
            nullValues: ['0', '0.00', attributesLocale.notFound],
            attributeSource: attributesLocale.groups.nutrients,
            attributes: [
                {
                    name: [
                        'energyKjPer100g',
                        'energyKjPer100ml',
                        'energyKjPer100gml'
                    ]
                },
                {
                    name: [
                        'energyKcalPer100g',
                        'energyKcalPer100ml',
                        'energyKcalPer100gml'
                    ]
                },
                {
                    name: [
                        'fatPer100g',
                        'fatPer100ml',
                        'fatPer100gml'
                    ]
                },
                {
                    name: [
                        'saturatedFattyAcidsPer100g',
                        'saturatedFattyAcidsPer100ml',
                        'saturatedFattyAcidsPer100gml'
                    ],
                    transformName: addDashPrefix
                },
                {
                    name: [
                        'monounsaturatedFattyAcidsPer100g',
                        'monounsaturatedFattyAcidsPer100ml',
                        'monounsaturatedFattyAcidsPer100gml'
                    ],
                    transformName: addDashPrefix
                },
                {
                    name: [
                        'polyunsaturatedFattyAcidsPer100g',
                        'polyunsaturatedFattyAcidsPer100ml',
                        'polyunsaturatedFattyAcidsPer100gml'
                    ],
                    transformName: addDashPrefix
                },
                {
                    name: [
                        'carbohydratesPer100g',
                        'carbohydratesPer100ml',
                        'carbohydratesPer100gml'
                    ]
                },
                {
                    name: [
                        'sugarsPer100g',
                        'sugarsPer100ml',
                        'sugarsPer100gml'
                    ],
                    transformName: addDashPrefix
                },
                {
                    name: [
                        'dietaryFibersPer100g',
                        'dietaryFibersPer100ml',
                        'dietaryFibersPer100gml'
                    ]
                },
                {
                    name: [
                        'proteinsPer100g',
                        'proteinsPer100ml',
                        'proteinsPer100gml'
                    ]
                },
                {
                    name: [
                        'saltPer100g',
                        'saltPer100ml',
                        'saltPer100gml'
                    ]
                }
            ]
        },
        {
            type: 'allergens',
            group: attributesLocale.groups.allergens,
            attributeSource: attributesLocale.groups.allergens,
            attributeSourceSet: [
                {
                    source: attributesLocale.groups.allergensContains,
                    value: attributesLocale.labels.yes
                },
                {
                    source: attributesLocale.groups.allergensCanContainTraces,
                    value: attributesLocale.labels.canContains
                },
                {
                    source: attributesLocale.groups.allergensDoesNotContain,
                    value: attributesLocale.labels.freeFrom
                }
            ],
            attributes: [
                'allergens.molluscs',
                'allergens.eggs',
                'allergens.fish',
                'allergens.gluten',
                {
                    name: 'allergens.barleyGluten',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.oatGluten',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.wheatGluten',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.khorasanWheatGluten',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.ryeGluten',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.speltGluten',
                    transformName: addLeftPaddingHTML
                },
                'allergens.lupin',
                'allergens.milk',
                'allergens.nuts',
                {
                    name: 'allergens.hazelnuts',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.cashews',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.macadamiaNuts',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.almonds',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.brazilNuts',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.peanuts',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.pecans',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.pistachios',
                    transformName: addLeftPaddingHTML
                },
                {
                    name: 'allergens.walnuts',
                    transformName: addLeftPaddingHTML
                },
                'allergens.celery',
                'allergens.mustard',
                'allergens.sesameSeeds',
                'allergens.shellfish',
                'allergens.soy',
                'allergens.sulphurDioxideOrSulfites'
            ],
            exclude: [
                attributesLocale.groups.allergens,
                attributesLocale.groups.allergensContains,
                attributesLocale.groups.allergensCanContainTraces,
                attributesLocale.groups.allergensDoesNotContain
            ],
            transform: (value) => {
                switch (value) {
                    case attributesLocale.values.contains:
                        return attributesLocale.labels.yes;
                    case attributesLocale.values.containsNot:
                        return attributesLocale.labels.freeFrom;
                    case attributesLocale.values.canContainTraces:
                        return attributesLocale.labels.canContains;
                    default:
                        return value;
                }
            }
        }
    ];
}
